import { DeviceType } from 'components/pages/Payment/paymentInstructionUtils';
import { DepositStatus } from 'data/payments/models';

export const AnalyticsEvents = {
  PAGE_VISIT: 'page-visit',
  SIGN_IN: 'sign-in',
  SIGN_OUT: 'sign-out',

  PAYMENT_PAGE_LOADED: 'payment_page_loaded',
  PAYMENT_CONFIRMED: 'payment_confirmed',
  PAYMENT_UPLOAD_BILL_CLICKED: 'payment_upload_bill_clicked',

  PAYMENT_CANCELLED: 'payment_cancelled',
  PAYMENT_RESULT: 'payment_result',
  PAYMENT_TIMER_EXPIRED: 'payment_timer_expired',
  PAYMENT_AMOUNT_COPIED: 'payment_amount_copied',
  PAYMENT_CREDENTIALS_COPIED: 'payment_credentials_copied',
  PAYMENT_BANK_COPIED: 'payment_bank_copied',
  PAYMENT_RECIPIENT_COPIED: 'payment_recipient_copied',
  PAYMENT_BILL_UPLOADED: 'payment_bill_uploaded',

  PAYMENT_LINK_CLICKED: 'payment_link_clicked',
  PAYMENT_QR_SAVED: 'payment_qr_saved',
  PAYMENT_BANK_PAY_LINK_CLICKED: 'payment_bank_pay_link_clicked',

  PAYMENT_ERROR: 'payment_error',
  PAYMENT_QR_LOAD_ERROR: 'qr_load_error',

  INSTRUCTIONS_OPENED: 'instructions_opened',
  INSTRUCTIONS_DEVICE_SELECTED: 'instructions_device_selected',
  INSTRUCTIONS_STEP_VIEWED: 'instructions_step_viewed',
  INSTRUCTIONS_CLOSED: 'instructions_closed',
} as const;

export type AnalyticsEventType =
  typeof AnalyticsEvents[keyof typeof AnalyticsEvents];

type DefaultPayload = Record<string, unknown>;

export interface AnalyticsEventPayloadMap {
  'page-visit': DefaultPayload;

  'sign-in': DefaultPayload;

  'sign-out': DefaultPayload;

  payment_page_loaded: {
    order_id: string;
    payment_method: string;
    amount_base: string;
    amount_local?: string;
    local_currency?: string;
  };

  payment_confirmed: {
    order_id: string;
    payment_method: string;
    amount_base: string;
  };

  payment_upload_bill_clicked: {
    order_id: string;
    payment_method: string;
    amount_base: string;
  };

  payment_cancelled: {
    order_id: string;
    payment_method: string;
    amount_base: string;
  };

  payment_result: {
    order_id: string;
    payment_method: string;
    status: DepositStatus;
    amount_base: string;
  };

  payment_timer_expired: {
    order_id: string;
    payment_method: string;
    amount_base: string;
  };

  payment_amount_copied: {
    order_id: string;
    payment_method: string;
    amount_base: string;
    amount_local?: string;
    local_currency?: string;
  };

  payment_credentials_copied: {
    order_id: string;
    payment_method: string;
    amount_base: string;
    amount_local?: string;
    local_currency?: string;
  };

  payment_bank_copied: {
    order_id: string;
    payment_method: string;
    amount_base: string;
    amount_local?: string;
    local_currency?: string;
  };

  payment_recipient_copied: {
    order_id: string;
    payment_method: string;
    amount_base: string;
    amount_local?: string;
    local_currency?: string;
  };

  payment_bill_uploaded: {
    order_id: string;
    payment_method: string;
    amount_base: string;
    amount_local?: string;
    local_currency?: string;
  };

  payment_link_clicked: {
    order_id: string;
    payment_method: string;
    amount_base: string;
  };

  payment_qr_saved: {
    order_id: string;
    payment_method: string;
    amount_base: string;
    bank?: string;
  };

  payment_bank_pay_link_clicked: {
    order_id: string;
    payment_method: string;
    amount_base: string;
    bank?: string;
  };

  instructions_opened: {
    order_id: string;
    payment_method: string;
  };

  instructions_device_selected: {
    order_id: string;
    payment_method: string;
    device: DeviceType;
  };

  instructions_step_viewed: {
    order_id: string;
    payment_method: string;
    device: DeviceType;
    step: string;
  };

  instructions_closed: {
    order_id: string;
    payment_method: string;
    device: DeviceType;
    last_step: string;
  };

  payment_error: {
    order_id: string;
    error_type: 'api_error' | 'network' | 'timeout';
    error_message: string;
    error_code?: string;
    http_status?: string | null;
  };

  qr_load_error: {
    order_id: string;
    error_message: string;
  };
}

export type InputType = 'amount' | 'credentials' | 'bank' | 'recipient';

type CopyEventType =
  | 'payment_amount_copied'
  | 'payment_credentials_copied'
  | 'payment_bank_copied'
  | 'payment_recipient_copied';

export const inputEventMap: Record<InputType, CopyEventType> = {
  amount: AnalyticsEvents.PAYMENT_AMOUNT_COPIED,
  credentials: AnalyticsEvents.PAYMENT_CREDENTIALS_COPIED,
  bank: AnalyticsEvents.PAYMENT_BANK_COPIED,
  recipient: AnalyticsEvents.PAYMENT_RECIPIENT_COPIED,
};
